import React, { useState, useEffect, useRef } from 'react';
import { TileLayout } from '@progress/kendo-react-layout';
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon, infoCircleIcon } from '@progress/kendo-svg-icons';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../messages/en-US';
import { Tooltip } from "@progress/kendo-react-tooltip";

interface Props {
  healthData: any;
  softwareFeatures: any;
}

export default function AccountHealthTiles({ healthData, softwareFeatures }: Props) {
  const localizationService = useLocalization();
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<any>(null);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [tileHeight, setTileHeight] = useState(500);

  useEffect(() => {
    const node = containerRef.current;
    const handleResize = () => {
      if (node) {
        setContainerWidth(node.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const observer = new window.ResizeObserver(handleResize);
    if (node) observer.observe(node);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (node) observer.unobserve(node);
    };
  }, []);

  useEffect(() => {
    const windowHeight = window.innerHeight;
    const headerHeight = 80;
    const pagePadding = 40;
    const tileGap = 15;
    const availableHeight = windowHeight - headerHeight - pagePadding - tileGap - 215;
    const calculatedHeight = Math.max(300, Math.min(availableHeight, 800));
    setTileHeight(calculatedHeight);
  }, [containerWidth]);

  const getColumnWidth = () => {
    if (containerWidth < 768) return containerWidth - 40;
    if (containerWidth < 1200) return (containerWidth - 60) / 2;
    return (containerWidth - 60) / 3;
  };

  const [columnWidth, setColumnWidth] = useState(getColumnWidth());

  useEffect(() => {
    setColumnWidth(getColumnWidth());
  }, [containerWidth]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'Online':
        return 'text-gbBrand-green-700';
      case 'Unhealthy':
      case 'Offline':
        return 'text-gbBrand-red-500';
      case 'Warning':
        return 'text-gbBrand-yellow-500';
      case 'Info':
        return 'text-gbBrand-blue-500';
      default:
        return 'text-gbBrand-blue-500';
    }
  };

  const getLocalizedMetricName = (metricName: string) => {
    switch (metricName) {
      case 'Users':
        return localizationService.toLanguageString('custom.users', enMessages.custom.users);
      case 'UsersRegistered':
        return localizationService.toLanguageString('custom.usersRegistered', enMessages.custom.usersRegistered);
      case 'GreaseTags':
        return localizationService.toLanguageString('custom.greaseTags', enMessages.custom.greaseTags);
      case 'MappedGreaseTags':
        return localizationService.toLanguageString('custom.mappedGreaseTags', enMessages.custom.mappedGreaseTags);
      case 'UnmappedGreaseTags':
        return localizationService.toLanguageString('custom.unmappedGreaseTags', enMessages.custom.unmappedGreaseTags);
      case 'GreaseTagsWithoutSchedule':
        return localizationService.toLanguageString('custom.greaseTagsWithoutSchedule', enMessages.custom.greaseTagsWithoutSchedule);
      case 'ExpectedTasks':
        return localizationService.toLanguageString('custom.expectedTasks', enMessages.custom.expectedTasks);
      case 'Endpoints':
        return localizationService.toLanguageString('custom.endpoints', enMessages.custom.endpoints);
      case 'MappedEndpoints':
        return localizationService.toLanguageString('custom.mappedEndpoints', enMessages.custom.mappedEndpoints);
      case 'UnmappedEndpoints':
        return localizationService.toLanguageString('custom.unmappedEndpoints', enMessages.custom.unmappedEndpoints);
      case 'EndpointsWithRequirements':
        return localizationService.toLanguageString('custom.endpointsWithRequirements', enMessages.custom.endpointsWithRequirements);
      case 'MappedEndpointsWithRequirements':
        return localizationService.toLanguageString('custom.mappedEndpointsWithRequirements', enMessages.custom.mappedEndpointsWithRequirements);
      case 'TotalHardwareDevices':
        return localizationService.toLanguageString('custom.totalHardwareDevices', enMessages.custom.totalHardwareDevices);
      case 'HeadUnitDevices':
        return localizationService.toLanguageString('custom.headUnitDevices', enMessages.custom.headUnitDevices);
      case 'HmeGatewayDevices':
        return localizationService.toLanguageString('custom.hmeGatewayCount', enMessages.custom.hmeGatewayCount);
      case 'EndpointMp2Devices':
        return localizationService.toLanguageString('custom.endpointMp2Devices', enMessages.custom.endpointMp2Devices);
        case 'EndpointBulkFlowDevices':
          return localizationService.toLanguageString('custom.endpointBulkFlowDevices', enMessages.custom.endpointBulkFlowDevices);
          case 'EndpointLowFlowDevices':
            return localizationService.toLanguageString('custom.endpointLowFlowDevices', enMessages.custom.endpointLowFlowDevices);
            case 'EndpointHighFlowDevices':
            return localizationService.toLanguageString('custom.endpointHighFlowDevices', enMessages.custom.endpointHighFlowDevices);
      default:
        return metricName;
    }
  };

  const transformData = (data: any) => {
    if (!data || !data.accountMetrics || !data.accountMetrics.metrics) return { stats: [], manual: [], automatic: [] };
    
    const stats: any[] = [];
    const manual: any[] = [];
    const automatic: any[] = [];
    
    data.accountMetrics.metrics.forEach((metric: any) => {
      const transformedMetric = {
        metric: getLocalizedMetricName(metric.name),
        value: metric.value,
        count: metric.count,
        status: metric.status,
        results: metric.count === -1 ? metric.value : `${metric.value}`
      };
      
      switch (metric.type) {
        case 'Account':
          stats.push(transformedMetric);
          break;
        case 'ManualSystem':
          if (softwareFeatures.isManualSystemEnabled) {
            manual.push(transformedMetric);
          }
          break;
        case 'AutomaticSystem':
          if (softwareFeatures.isAutomaticSystemEnabled) {
            automatic.push(transformedMetric);
          }
          break;
        default:
          stats.push(transformedMetric);
      }
    });
    
    return { stats, manual, automatic };
  };
  
  const { stats, manual, automatic } = transformData(healthData);

  const getColumns = () => {
    if (containerWidth < 768) return 1;
    
    // Count how many tiles will be displayed
    let tileCount = 1; // Stats tile is always shown
    if (softwareFeatures.isManualSystemEnabled) tileCount++;
    if (softwareFeatures.isAutomaticSystemEnabled) tileCount++;
    
    // Return appropriate number of columns based on tile count
    if (tileCount === 1) return 1;
    if (tileCount === 2) return 2;
    return 3;
  };

  const columns = getColumns();

  const TooltipContent = () => (
    <div className="p-2">
      <div className="font-semibold mb-2">{localizationService.toLanguageString('custom.statusIndicators', enMessages.custom.statusIndicators)}:</div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="medium" className="text-gbBrand-green-700" />
          <span>{localizationService.toLanguageString('custom.healthy', enMessages.custom.healthy)}</span>
        </div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="medium" className="text-gbBrand-red-500" />
          <span>{localizationService.toLanguageString('custom.unhealthy', enMessages.custom.unhealthy)}</span>
        </div>
        <div className="flex items-center gap-2">
          <SvgIcon icon={circleIcon} size="medium" className="text-gbBrand-blue-500" />
          <span>{localizationService.toLanguageString('custom.information', enMessages.custom.information)}</span>
        </div>
      </div>
    </div>
  );

  const MetricItem = ({ item }: { item: any }) => (
    <div className="flex items-center justify-between py-3 px-1 border-b border-gray-100 last:border-b-0">
      <span className="text-base font-medium text-gray-700 flex-1">{item.metric}</span>
      <div className="flex items-center gap-3 justify-end">
        <span className="text-base text-gray-600 font-medium text-right">
          {(item.count === -1 && item.value === -1) ? '-' : (item.count === -1 ? item.value : item.results)}
        </span>
        <div className="flex justify-center">
          <SvgIcon icon={circleIcon} size="xlarge" className={getStatusColor(item.status)} />
        </div>
      </div>
    </div>
  );

  const StatsTile = () => (
    <div className="h-full p-4 flex flex-col">
      <h3 className="text-xl font-semibold mb-2 text-gray-800">{localizationService.toLanguageString('custom.details', enMessages.custom.details)}</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {stats.length > 0 ? (
          stats.map((item, index) => (
            <MetricItem key={index} item={item} />
          ))
        ) : (
          <div className="text-base text-gray-500 text-center py-8">No stats available</div>
        )}
      </div>
    </div>
  );

  const ManualSystemTile = () => (
    <div className="h-full p-4 flex flex-col">
      <h3 className="text-xl font-semibold mb-2 text-gray-800">{localizationService.toLanguageString('custom.manualSystem', enMessages.custom.manualSystem)}</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {manual.length > 0 ? (
          manual.map((item, index) => (
            <MetricItem key={index} item={item} />
          ))
        ) : (
          <div className="text-base text-gray-500 text-center py-8">
            {softwareFeatures.isManualSystemEnabled ? 'No manual system metrics available' : 'Manual system not enabled'}
          </div>
        )}
      </div>
    </div>
  );

  const AutomaticSystemTile = () => (
    <div className="h-full p-4 flex flex-col">
      <h3 className="text-xl font-semibold mb-2 text-gray-800">{localizationService.toLanguageString('custom.automaticSystem', enMessages.custom.automaticSystem)}</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {automatic.length > 0 ? (
          automatic.map((item, index) => (
            <MetricItem key={index} item={item} />
          ))
        ) : (
          <div className="text-base text-gray-500 text-center py-8">
            {softwareFeatures.isAutomaticSystemEnabled ? 'No automatic system metrics available' : 'Automatic system not enabled'}
          </div>
        )}
      </div>
    </div>
  );

  const dashboardTileData = [
    {
      body: <StatsTile />,
      reorderable: false,
    },
    ...(softwareFeatures.isManualSystemEnabled ? [{
      body: <ManualSystemTile />,
      reorderable: false,
    }] : []),
    ...(softwareFeatures.isAutomaticSystemEnabled ? [{
      body: <AutomaticSystemTile />,
      reorderable: false,
    }] : []),
  ];

  const columnsDisplayPosition = dashboardTileData.map((_, index) => ({
    col: (index % columns) + 1,
    colSpan: 1,
    rowSpan: 1,
    row: Math.floor(index / columns) + 1
  }));

  return (
    <>
      <div className="flex items-baseline gap-1">
        <div className="text-xl font-bold p-4 pb-0 pr-0">{localizationService.toLanguageString('custom.accountHealth', enMessages.custom.accountHealth)}</div>
        <Tooltip
          ref={tooltipRef}
          anchorElement="target"
          position="bottom"
          parentTitle={true}
          content={TooltipContent}
        >
          <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }} title=" ">
            <SvgIcon icon={infoCircleIcon} size="small" />
          </span>
        </Tooltip>
      </div>
      <div ref={containerRef} style={{ width: "100%" }}>
        <TileLayout
          columns={columns}
          rowHeight={tileHeight}
          columnWidth={columnWidth}
          positions={columnsDisplayPosition}
          gap={{ rows: 15, columns: 15 }}
          items={dashboardTileData}
          className="dashboard-page-tile"
        />
      </div>
    </>
  );
}
